<template>
  <v-row dense>
    <v-col cols="12" sm="4" :md="labelSize" v-if="label && !hideLabel">
      <v-card flat>
        <span class="text-subtitle-2">{{ label }}</span>
      </v-card>
      <div v-if="hint">
        <span class="text-caption">{{ hint }}</span>
      </div>
      <v-card v-if="nullable" flat>
        <v-switch v-model="nullValue" dense>
          <template #label>
            <p class="my-auto text-caption">
              {{ switchText }}
            </p>
          </template>
        </v-switch>
      </v-card>
    </v-col>
    <template v-if="!columnLayout">
      <v-col class="grow" v-if="!nullable || (nullable && nullValue)">
        <v-text-field
          v-model.number="computedValue[0]"
          label="Latitude"
          hint="42.236"
          persistent-hint
          :rules="[checkLat]"
          @change="$emit('change')"
        />
        <v-text-field
          v-model.number="computedValue[1]"
          label="Longitude"
          hint="92.236"
          persistent-hint
          :rules="[checkLong]"
          @change="$emit('change')"
        />
        <slot name="append-row"></slot>
      </v-col>
      <v-col class="grow" v-if="!nullable || (nullable && nullValue)">
        <div :style="`height: ${mapHeight}px`">
          <LMap
            :zoom="zoom"
            :center="center"
            style="height: 100%; width: 100%; z-index: 0"
            :options="{ attributionControl: false }"
          >
            <LTileLayer :url="url" />
            <LMarker :lat-lng="center" draggable @update:latLng="markerUpdate" :icon="markerIcon" />
          </LMap></div
      ></v-col>
    </template>
    <template v-else-if="columnLayout">
      <v-col cols="12" v-if="!nullable || (nullable && nullValue)">
        <div>
          <v-responsive v-bind="mapAttrs">
            <LMap
              :zoom="zoom"
              :center="center"
              style="height: 100%; width: 100%; z-index: 0"
              :options="{ attributionControl: false }"
            >
              <LTileLayer :url="url" />
              <LMarker :lat-lng="center" draggable @update:latLng="markerUpdate" />
              <LControl position="bottomright" class="logo-control-2">
                <img src="@/assets/rex-logo.png" alt="Acelerex" class="logo-2" />
              </LControl>
            </LMap>
          </v-responsive>
        </div>
      </v-col>
      <template v-if="!nullable || (nullable && nullValue)">
        <v-col cols="6">
          <v-text-field
            v-model.number="computedValue[0]"
            label="Latitude"
            hint="42.236"
            persistent-hint
            :rules="[checkLat]"
            @change="$emit('change')"
          />
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model.number="computedValue[1]"
            label="Longitude"
            hint="92.236"
            persistent-hint
            :rules="[checkLong]"
            @change="$emit('change')"
          />
        </v-col>
      </template>
    </template>
  </v-row>
</template>

<script>
import { customIcon } from "@/plugins/map-utils";
import { LControl, LMap, LMarker, LTileLayer } from "vue2-leaflet";

export default {
  name: "InputGeo",
  components: { LControl, LMap, LTileLayer, LMarker },
  mixins: [],
  props: {
    value: { type: Array, required: false, default: () => [null, null] },
    label: { type: String, required: false, default: null },
    mapHeight: { type: [Number, String], required: false, default: 200 },
    hideLabel: { type: Boolean, required: false, default: false },
    hint: {
      type: String,
      required: false,
      default: null,
    },
    labelSize: {
      type: Number,
      required: false,
      default: 4,
    },
    nullable: {
      type: Boolean,
      required: false,
      default: false,
    },
    switchText: {
      type: String,
      required: false,
      default: "Enable",
    },
    columnLayout: {
      type: Boolean,
      required: false,
      default: false,
    },
    responsive: {
      type: Boolean,
      required: false,
      default: false,
    },
    icon: { type: String, required: false, default: "default" },
    iconColor: { type: String, required: false, default: "#306d8a" },
    initialCoordinates: { type: Array, required: false, default: () => [40.71, -74] },
  },
  data() {
    return {
      nullValue: !(this.value[0] === null && this.value[1] === null),
      zoom: 9,
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    };
  },
  computed: {
    computedValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
        this.$emit("change");
      },
    },
    center() {
      if (this.checkLat(this.value[0]) === true && this.checkLong(this.value[1]) === true) {
        return this.value;
      }
      return this.initialCoordinates;
    },
    mapAttrs() {
      return { ...(this.responsive ? { "aspect-ratio": 1 / 1 } : { height: this.mapHeight }) };
    },
    markerIcon() {
      const pinFillColor = this.iconColor || "#306d8a";
      return customIcon(this.icon, pinFillColor);
    },
  },
  watch: {
    nullValue(val) {
      if (!val) {
        this.$emit("input", [null, null]);
        this.$emit("change");
      } else {
        this.$emit("input", this.initialCoordinates);
        this.$emit("change");
      }
    },
  },
  beforeCreate() {},
  created() {},
  beforeMount() {},
  mounted() {},
  beforeUpdate() {},
  updated() {},
  activated() {},
  deactivated() {},
  beforeDestroy() {},
  destroyed() {},
  methods: {
    customIcon,
    checkLat: (val) =>
      /^(-?[1-8]?\d(?:\.\d{1,18})?|90(?:\.0{1,18})?)$/.test(val.toString()) ||
      "Enter valid latitude",
    checkLong: (val) =>
      /^(-?(?:1[0-7]|[1-9])?\d(?:\.\d{1,18})?|180(?:\.0{1,18})?)$/.test(val.toString()) ||
      "Enter valid longitude",
    markerUpdate(val) {
      this.$emit("input", [val.lat.toFixed(3), val.lng.toFixed(3)]);
    },
  },
};
</script>

<style>
.logo-2 {
  max-height: 24px;
  opacity: 0.8;
}
.logo-control-2 {
  opacity: 0.8;
  padding-bottom: 0px;
  text-align: end;
  margin-bottom: 0px !important;
  margin-right: 4px !important;
}
</style>
